/**
 * Riga di sistema per le chiamate WhatsApp (messaggio di tipo 'call_log').
 * Righe del core (inbound/handlers/call.ts): metadata { kind: 'call', isVideo, call_status }
 * con call_status received | accepted | rejected | missed. Righe vecchie: content JSON
 * { is_video, status, from_me } oppure testo gia' pronto. Parsing in messageExtras.ts.
 * Colori: persa in rosso, rifiutata in grigio, ricevuta/risposta normale.
 */

import { memo } from 'react';
import { Phone, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, Video, VideoOff } from 'lucide-react';
import { parseCallLog, formatCallLog, callLogFromMetadata, type CallTone } from './messageExtras';

interface CallLogBubbleProps {
  content?: string;
  timestamp: Date;
  direction?: 'inbound' | 'outbound';
  metadata?: Record<string, unknown>;
}

const TONE_ICON: Record<CallTone, string> = {
  missed: 'text-red-500',
  rejected: 'text-[#9B9A97]',
  normal: 'text-[#787774]',
};

const TONE_TEXT: Record<CallTone, string> = {
  missed: 'text-red-600 dark:text-red-400',
  rejected: 'text-[#9B9A97] dark:text-white/50',
  normal: 'text-[#37352F] dark:text-white/90',
};

export const CallLogBubble = memo(function CallLogBubble({ content, timestamp, direction, metadata }: CallLogBubbleProps) {
  const view = callLogFromMetadata(metadata, timestamp);

  let text: string;
  let tone: CallTone;
  let Icon: typeof Phone;
  if (view) {
    text = view.text;
    tone = view.tone;
    if (view.isVideo) Icon = view.tone === 'normal' ? Video : VideoOff;
    else Icon = view.tone === 'missed' ? PhoneMissed : view.tone === 'rejected' ? PhoneOff : PhoneIncoming;
  } else {
    const info = parseCallLog(content, direction);
    text = typeof info === 'string' ? info : formatCallLog(info, timestamp);
    const missed = typeof info !== 'string' && !info.outgoing && info.status !== 'accepted' && info.status !== 'answered';
    tone = missed ? 'missed' : 'normal';
    Icon = typeof info !== 'string' && info.isVideo
      ? Video
      : typeof info !== 'string' && info.outgoing ? PhoneOutgoing : missed ? PhoneMissed : Phone;
  }

  return (
    <div className="flex justify-center my-1" role="note">
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 shadow-sm text-xs ${TONE_TEXT[tone]}`}>
        <Icon className={`w-3.5 h-3.5 ${TONE_ICON[tone]}`} aria-hidden />
        <span>{text}</span>
      </div>
    </div>
  );
});
